import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import sessionService from '../../services/SessionService';
import { Button } from '../Common/Button';
import EventEmitter from 'eventemitter3';
import Events from './Events';
import { Fullscreen, ContentPanel, Footer, Section } from '../Common/Modal/Fullscreen';
import LogoPanel from './LogoPanel';
import {
  FUNNEL,
} from '../../interfaces/ProductService.interface';
import { i18nConfig } from '../../assets';
import { useIntl } from 'react-intl';
import { ShopSettingsContext } from '../../FootprintTech/AppController';
import log, { LogMode } from '../../services/LogService';
import { arrowIcon, caret, infoIcon, langIcon } from '../../assets/Images';
import { colors } from '../../theme/colors';
import {version} from '../../../../package.json';

interface SplashscreenProps {
  emitter: EventEmitter;
  funnel: FUNNEL;
}

const Welcome = styled.div`
  font: 600 14px Inter, sans-serif;
  margin-bottom: 18px;
  margin-top: 3vh;
`;

const Text = styled.div`
  font: normal 600 18px Inter, sans-serif;
  margin-bottom: 25px;
  color: #000;
`;

const CustomSelect = styled.select`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 16px !important;
  padding: 12px 40px 12px 40px !important;
  background-color: rgba(255, 255, 255, 0.01) !important;
  background-image: url(${langIcon}) !important;
  background-repeat: no-repeat;
  background-position: left 12px center;
  background-size: 24px 24px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: solid 2px ${colors['inactive-blue']};
  border-radius: 12px;
  width: auto;
  text-align: center;
  color: #000;

  &>option {
    font-family: Inter, sans-serif;
    font-weight: 500;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  color: #000;
  gap: 10px;
`;

const InfoIcon = styled.img`
  width: 18px;
  height: 18px;
  margin: auto;
`;

const InfoMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoLine = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 13px;
  text-align: left;
`;

const LangSelect = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  min-height: 47px;
  margin: 0 0 24px;
`;

const Caret = styled.img`
  display: block;
  width: 16px;
  margin: 0 0 0 -30px;
  pointer-events: none;
`;

const Disclaimer = styled.div`
  @media(height > 600px) {
    display: none;
  }
`;

const DisclaimerSection = styled.div`
  margin: 0 auto 16px;
`;

const handleCaretClick = () => {
  console.log('caret clicked');
  document.querySelector('select').click();
}

const Splashscreen: React.FunctionComponent<SplashscreenProps> = (props) => {
  const { emitter, funnel } = props;
  const intl = useIntl();
  const shopSettings = useContext(ShopSettingsContext);
  const [aknowledged, setAknowledged] = useState(false);

  const handlePrimary = (ev) => {

    // This is to prevent banner from handling click event
    ev.nativeEvent.stopImmediatePropagation();

    if (funnel === FUNNEL.UNDEFINED || funnel === FUNNEL.SELF) {
      log.info(`SELF funnel selected`);
      return emitter.emit(Events.onFunnelSelect, 'SELF');
    }

    log.info('OTHER funnel selected');
    emitter.emit(Events.onFunnelSelect, 'OTHER');
  };

  const handleSecondary = (ev) => {

    // This is to prevent banner from handling click event
    ev.nativeEvent.stopImmediatePropagation();

    log.info('Handle secondary');
    emitter.emit(Events.onFunnelSelect, 'OTHER');
  };

  const handleResize = () => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    log.info(`resize ${vh}`);
  };

  const handleLanguageSelect = ev => {
    ev.nativeEvent.stopImmediatePropagation();
    const selectedLang = ev.target.value;
    log.info(`Language ${selectedLang} selected in Splashscreen ${ev.target}`);
    emitter.emit(Events.onLanguageSelect, selectedLang);
  };

  const handleAknowledged = ev => {
    setAknowledged(true);
    document.querySelectorAll('button.hide-lt-600')
    .forEach(
      (el:HTMLElement) => el.classList.remove('hide-lt-600')
    );
  };

  useEffect(() => {

    sessionService.updateExitCode('PDP', 'splashscreen.displayed');
    // We listen to the resize event
    window.addEventListener('resize', handleResize)

    // The cleanup function
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, []);

  return (
    <Fullscreen emitter={emitter}>
      <LangSelect>
    { shopSettings.languageSelectEnabled &&
      <>
        <CustomSelect onChange={handleLanguageSelect} value={intl.locale}>
        {
          Object.keys(i18nConfig).map(key => <option key={key} value={key}>{i18nConfig[key].name}</option>)
        }
        </CustomSelect>
        <Caret src={caret} />
      </>
    }
      </LangSelect>
      <LogoPanel />
      <ContentPanel>
        <Text>
          <FormattedMessage id="drv.splashscreen.text" />
        </Text>
        { !aknowledged &&
          <Disclaimer className="disclaimer">
            <DisclaimerSection>
              <FormattedMessage id="drv.splashscreen.footer"
                values={{
                  policy: (chunks) => <a href="https://footprinttech.de/privacy-policy-webapp/" target="_blank">{chunks}</a>,
                  tos: (chunks) => <a href="https://footprinttech.de/terms-of-use-webapp/" target="_blank">{chunks}</a>
                }} />
            </DisclaimerSection>
            <DisclaimerSection>
              <FormattedMessage id="drv.splashscreen.imprint"
                values={{
                  imp: (chunks) => <a href="https://footprinttech.de/imprint-webapp/" target="_blank">{chunks}</a>,
                }} />
            </DisclaimerSection>
            <DisclaimerSection>
              <Button onClick={handleAknowledged} type="button" className="wide-primary">
                <FormattedMessage id="common.next" />
              </Button>
            </DisclaimerSection>
          </Disclaimer>
        }
        <Button onClick={handlePrimary} type="button" className="wide-primary hide-lt-600">
          <FormattedMessage id="drv.splashscreen.option.adult" />
        </Button>
        {funnel === FUNNEL.UNDEFINED &&
          <Button onClick={handleSecondary} type="button" className="wide-secondary hide-lt-600">
            <FormattedMessage id="drv.splashscreen.option.child" />
          </Button>
        }
        <Info className="hide-lt-700">
          <InfoIcon src={infoIcon} />
          <InfoMessage>
            <InfoLine><FormattedMessage id="drv.splashscreen.instructions.paper" values={{
              b: (chunks) => <b>{chunks}</b>,
            }} /></InfoLine>
            <InfoLine><FormattedMessage id="drv.splashscreen.instructions.time" values={{
              b: (chunks) => <b>{chunks}</b>,
            }}/></InfoLine>
          </InfoMessage>
        </Info>
      </ContentPanel>
      <Footer className="hide-lt-600 disclaimer">
        { log.mode === LogMode.VERBOSE &&
          <Section>Driver version: {version}</Section>
        }
        <Section>
          <FormattedMessage id="drv.splashscreen.footer"
            values={{
              policy: (chunks) => <a href="https://footprinttech.de/privacy-policy-webapp/" target="_blank">{chunks}</a>,
              tos: (chunks) => <a href="https://footprinttech.de/terms-of-use-webapp/" target="_blank">{chunks}</a>
            }} />
        </Section>
        <Section>
          <FormattedMessage id="drv.splashscreen.imprint"
            values={{
              imp: (chunks) => <a href="https://footprinttech.de/imprint-webapp/" target="_blank">{chunks}</a>,
            }} />
        </Section>
      </Footer>
    </Fullscreen>
  )
};

export default Splashscreen;
